import type { ICodiceFiscaleObject } from "codice-fiscale-js";
import CodiceFiscale from "codice-fiscale-js";
import isString from "lodash/isString";
import moment, { type Moment } from "moment";
import type { FieldError } from "react-hook-form";
import { baseNationCollection } from "../../assets/collections/baseNationCollection";
import { belfioreToCountryCodeMap } from "../../assets/collections/belfioreNationCollection";
import { COMPLETE_PROFILE_ERROR_TEXT, FISCAL_CODE_FROM_AGE_TEXT } from "../../constants/commonStrings";
import { type IRegistrySex } from "../interfaces/IRegistrySex";

export const INSERT_VALID_BIRTH_DATE_TEXT = "Inserisci una data di nascita valida";
export const BIRTH_DATE_NOT_IN_FUTURE_TEXT = "La data di nascita non può essere nel futuro";
export const IS_ADULT_TEXT = "Per usare BonusX, bisogna essere maggiorenni.";

const monthFromTaxCode: Record<string, string> = {
  A: "01",
  B: "02",
  C: "03",
  D: "04",
  E: "05",
  H: "06",
  L: "07",
  M: "08",
  P: "09",
  R: "10",
  S: "11",
  T: "12",
};

export const MINIMUM_ACCEPTED_BIRTH_DATE = new Date("1900-01-01");

export function getBirthDateFromTaxCode(taxCode?: string): Date {
  if (typeof taxCode !== "string" || !CodiceFiscale.check(taxCode)) {
    throw new Error("TaxCode is not valid");
  }

  const taxCodeParsed = CodiceFiscale.computeInverse(taxCode);

  const date = moment(`${taxCodeParsed.day}/${taxCodeParsed.month}/${taxCodeParsed.year}`, "DD/MM/YYYY");

  return date.toDate();
}

export function ageGreaterThan18YearFromTaxCode(taxCode: string): boolean {
  if (typeof taxCode !== "string" || !CodiceFiscale.check(taxCode)) {
    throw new Error("TaxCode is not valid");
  }

  const birthDate = computeBirthDateFromTaxCode(taxCode);
  const diffYear = moment().diff(birthDate, "years", true) || 0;
  return diffYear >= 18;
}

export function validateBirthDate(birthDate: Date | string | undefined, shouldBeAnAdult: boolean): string | boolean {
  if (!birthDate) {
    return INSERT_VALID_BIRTH_DATE_TEXT;
  }

  let birthDateToValidate: Date;
  if (typeof birthDate === "string") {
    birthDateToValidate = new Date(birthDate);
  } else {
    birthDateToValidate = birthDate;
  }

  const isAboveMinimumAcceptedDate = isBirthDateAboveMinimumAcceptedDate(birthDateToValidate);

  if (!isAboveMinimumAcceptedDate) {
    return INSERT_VALID_BIRTH_DATE_TEXT;
  }

  const isDateBirthLowerThanToday = birthDateToValidate < new Date();

  if (!isDateBirthLowerThanToday) {
    return BIRTH_DATE_NOT_IN_FUTURE_TEXT;
  }

  let isAdult = true;
  if (shouldBeAnAdult) {
    isAdult = ageGreaterOrEqualThan18YearFromDate(birthDateToValidate);
    if (!isAdult) {
      return IS_ADULT_TEXT;
    }
  }

  return isAdult;
}

export function ageGreaterOrEqualThan18YearFromDate(birthDate: Date): boolean {
  if (!birthDate) {
    return true;
  }

  const diffYear = moment().diff(birthDate, "years", true) || 0;

  return diffYear >= 18;
}

export function isBirthDateAboveMinimumAcceptedDate(birthDate: Date) {
  if (!birthDate) {
    return false;
  }
  return birthDate >= MINIMUM_ACCEPTED_BIRTH_DATE;
}

export function isValidTaxCodeOfAdult(taxCode: string): boolean {
  return CodiceFiscale.check(taxCode) && ageGreaterThan18YearFromTaxCode(taxCode);
}

export function computeBirthDateFromTaxCode(taxCode: string, priorityToAdult: boolean = true): Date {
  if (typeof taxCode !== "string" || !CodiceFiscale.check(taxCode)) {
    throw new Error("TaxCode is not valid");
  }
  let year;
  const taxCodeUpperCase = upperCaseTaxCode(taxCode);
  const actualYear = moment().year();

  const yearSubstring = taxCodeUpperCase.substring(6, 8);
  const yearRaw = Number(yearSubstring) + 2000;
  const minYearForAdult = actualYear - 18;

  const isComputeTaxCodeForAdultAndisYearComputedForMinor = priorityToAdult && yearRaw > minYearForAdult;
  const isYearComputedFromFuture = yearRaw > actualYear;
  if (isComputeTaxCodeForAdultAndisYearComputedForMinor || isYearComputedFromFuture) {
    year = yearRaw - 100;
  } else {
    year = yearRaw;
  }

  const monthChar = taxCodeUpperCase[8];
  const monthString = monthFromTaxCode[monthChar];

  // month had been decreased by one because new Date() increase the month by one.
  const month = Number(monthString) - 1;

  const dayString = taxCodeUpperCase[9] + taxCodeUpperCase[10];
  const dayRaw = Number(dayString);
  const day = dayRaw > 40 ? dayRaw - 40 : dayRaw;

  const fullDate = new Date(year, month, day);

  return fullDate;
}

function upperCaseTaxCode(taxCode: string): string {
  if (typeof taxCode !== "string") {
    throw new Error("TaxCode is not valid");
  }
  return taxCode.toUpperCase();
}

export function areDayAndMonthEqual(dateFromTaxCode: Moment, dateFromQuestionnaire: Moment): boolean {
  const areDaysEqual = dateFromTaxCode.date() === dateFromQuestionnaire.date();
  const areMonthsEqual = dateFromTaxCode.month() + 1 === dateFromQuestionnaire.month() + 1;

  return areDaysEqual && areMonthsEqual;
}

export function areYearsEqual(dateFromTaxCode: Moment, dateFromQuestionnaire: Moment): boolean {
  const dateFromTaxCodeComputedPlus100Years = moment(dateFromTaxCode, "DD/MM/YYYY").add(100, "years");

  const dateFromTaxCodeComputedSubtract100Years = moment(dateFromTaxCode, "DD/MM/YYYY").subtract(100, "years");

  const dateFromQuestionnaireYear = dateFromQuestionnaire.year();
  const dateFromTaxCodeYear = dateFromTaxCode.year();

  const areYearsEqual =
    dateFromTaxCodeYear === dateFromQuestionnaireYear ||
    dateFromTaxCodeComputedPlus100Years.year() === dateFromQuestionnaireYear ||
    dateFromTaxCodeComputedSubtract100Years.year() === dateFromQuestionnaireYear;

  return areYearsEqual;
}

export function areDatesEqual(dateFromTaxCode: string, dateFromQuestionnaire: string): boolean {
  const dateFromTaxCodeComputed = moment(dateFromTaxCode, "DD/MM/YYY");
  const dateFromQuestionnaireComputed = moment(dateFromQuestionnaire, "DD/MM/YYY");

  if (!areDayAndMonthEqual(dateFromTaxCodeComputed, dateFromQuestionnaireComputed)) {
    return false;
  }

  const areSecondPartEqual = areYearsEqual(dateFromTaxCodeComputed, dateFromQuestionnaireComputed);

  return areSecondPartEqual;
}

export function checkIsValidTaxCode(taxCode?: string): void | never {
  const isValidTaxCode = isString(taxCode) && CodiceFiscale.check(taxCode);
  if (!isValidTaxCode) {
    throw new Error("TaxCode is not valid");
  }
}

export function getRegistrySexFromTaxCode(taxCode?: string): IRegistrySex {
  checkIsValidTaxCode(taxCode);

  const { gender } = CodiceFiscale.computeInverse(taxCode as string);
  return gender;
}

export function getBirthPlaceFromTaxCode(taxCode?: string): string {
  checkIsValidTaxCode(taxCode);

  const { birthplace: birthPlace } = CodiceFiscale.computeInverse(taxCode as string);
  return birthPlace;
}

export function getDataFromTaxCode(taxCode?: string): ICodiceFiscaleObject {
  checkIsValidTaxCode(taxCode);

  const taxCodeData = CodiceFiscale.computeInverse(taxCode as string);
  return taxCodeData;
}

const defaultItalianCountryCode = "IT";
// https://github.com/lucavandro/CodiceFiscaleJS/blob/1882747216330b1fd65769239c8c71aff59da13c/src/lista-province.js#L110C6-L110C12
const abroadBirthplaceProvincia = "EE";

export function getCountryCodeFromTaxCode(taxCode?: string): string {
  const isValidTaxCode = isString(taxCode) && CodiceFiscale.check(taxCode);

  if (!isValidTaxCode) {
    throw new Error("TaxCode is not valid");
  }

  const { birthplace } = new CodiceFiscale(taxCode);
  const isExternalCountryCitizenship = birthplace.prov === abroadBirthplaceProvincia;

  if (isExternalCountryCitizenship) {
    const countryCodeFromBelfiore = belfioreToCountryCodeMap.get(birthplace.cc);
    if (countryCodeFromBelfiore) {
      return countryCodeFromBelfiore;
    }

    const birthplaceLowerCase = birthplace.nome.toLowerCase();

    const country = baseNationCollection.find(function findCountryCodeFromCountryName(currentCountry) {
      return currentCountry.name.toLowerCase() === birthplaceLowerCase;
    });

    if (country) {
      return country.value;
    }
  }

  return defaultItalianCountryCode;
}

export function taxCodeErrorLabel(error: FieldError | undefined, taxCode: string): string | null {
  if (CodiceFiscale.check(taxCode) && !ageGreaterThan18YearFromTaxCode(taxCode)) {
    return FISCAL_CODE_FROM_AGE_TEXT;
  }
  return error ? COMPLETE_PROFILE_ERROR_TEXT : null;
}
